// Wrapper fetch vers api.ligobox.com pour la vitrine.
// Toutes les routes /api/vitrine/* sont publiques (sans auth).

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.ligobox.com';
export const APP_URL = process.env.NEXT_PUBLIC_APP_URL || 'https://app.ligobox.com';

// ─── Stratégie de cache ────────────────────────────────────────────────
// Au BUILD (Next.js export statique côté serveur), il faut `force-cache`
// sinon Next.js considère la page dynamique → erreur de build avec
// `output: 'export'`. Au RUNTIME (navigateur), `force-cache` est mauvais :
// le navigateur garde les vieilles données et il faut rafraîchir N fois
// après une édition admin pour voir les changements.
//
// On détecte le contexte via `typeof window` :
//   - server (build) → force-cache (HTML statique baked)
//   - client (browser) → no-store + cache-bust query (toujours frais)
//
// Conséquence : édite dans l'admin, recharge la page une seule fois → tu
// vois la nouvelle version.
const isServer = typeof window === 'undefined';
const CACHE_OPT = isServer ? 'force-cache' : 'no-store';
function cacheBust(url) {
  if (isServer) return url;
  // Ajoute un timestamp query pour contourner les caches HTTP intermediaires
  // (CDN/nginx) qui pourraient ignorer cache-control: no-store.
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}_t=${Date.now()}`;
}

/**
 * Recupere les reglages de la vitrine (couleurs, logo, textes, etc.)
 *
 * IMPORTANT : retourne `null` si l'API echoue (timeout, 5xx, reseau down).
 * L'appelant DOIT gerer ce cas en gardant les anciens settings — sinon
 * le logo/favicon configures peuvent disparaitre de l'UI au moindre hoquet
 * reseau.
 *
 * Pour avoir des valeurs par defaut quand on est en *premier* chargement
 * (pas encore de settings en mémoire), utiliser `defaultVitrineSettings()`.
 */
export async function getVitrineSettings() {
  try {
    const res = await fetch(cacheBust(`${API_URL}/api/vitrine/settings`), {
      cache: CACHE_OPT,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (err) {
    console.warn('[vitrine] getVitrineSettings failed:', err.message);
    return null; // ← NE PAS retourner les defaults : laisser l'appelant decider
  }
}

/** Defaults — utilises uniquement au tout premier chargement, jamais comme fallback. */
export function defaultVitrineSettings() {
  return defaultSettings();
}

/**
 * Recupere les packs visibles sur la vitrine (active_on_site=true).
 */
export async function getVitrinePacks() {
  try {
    const res = await fetch(cacheBust(`${API_URL}/api/vitrine/packs`), {
      cache: CACHE_OPT,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return data.packs || [];
  } catch (err) {
    console.warn('[vitrine] getVitrinePacks failed:', err.message);
    return [];
  }
}

/**
 * Soumet le formulaire de contact.
 */
export async function submitContact(payload) {
  const res = await fetch(`${API_URL}/api/vitrine/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Erreur HTTP ${res.status}`);
  }
  return await res.json();
}

/**
 * Construit l'URL de signup avec un pack pre-selectionne.
 */
export function buildSignupUrl(packId) {
  if (!packId) return `${APP_URL}/signup`;
  return `${APP_URL}/signup?pack_id=${encodeURIComponent(packId)}`;
}

/**
 * Recupere la liste des pages CMS publiees (CGV, mentions, etc.).
 * Utilise par Navbar et Footer pour afficher les liens dynamiques.
 */
export async function getVitrinePages() {
  try {
    const res = await fetch(cacheBust(`${API_URL}/api/vitrine/pages`), { cache: CACHE_OPT });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return data.pages || [];
  } catch (err) {
    console.warn('[vitrine] getVitrinePages failed:', err.message);
    return [];
  }
}

/**
 * V1.25.949 — Recupere la page CMS marquee comme page d'accueil (is_home=true).
 * Renvoie null si aucune page d'accueil n'est definie. Utilise par app/page.jsx
 * pour rendre les blocs CMS au lieu du contenu hardcode.
 */
export async function getVitrineHomePage() {
  try {
    const res = await fetch(cacheBust(`${API_URL}/api/vitrine/pages/_home`), { cache: CACHE_OPT });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (err) {
    console.warn('[vitrine] getVitrineHomePage failed:', err.message);
    return null;
  }
}

/**
 * Recupere une page CMS par son slug. Renvoie null si non publiee/inexistante.
 */
export async function getVitrinePage(slug) {
  try {
    const res = await fetch(
      cacheBust(`${API_URL}/api/vitrine/pages/${encodeURIComponent(slug)}`),
      { cache: CACHE_OPT },
    );
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (err) {
    console.warn('[vitrine] getVitrinePage failed:', err.message);
    return null;
  }
}

/**
 * Blog Ligobox — index des articles publiés (sans corps), catégories et textes
 * d'en-tête. Lu au build par app/blog/page.jsx et app/sitemap.js.
 * Renvoie { articles: [], categories: [], settings: {} } si l'API échoue.
 */
export async function getVitrineBlogIndex() {
  try {
    const res = await fetch(cacheBust(`${API_URL}/api/vitrine/blog`), { cache: CACHE_OPT });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return {
      articles: Array.isArray(data.articles) ? data.articles : [],
      categories: Array.isArray(data.categories) ? data.categories : [],
      settings: data.settings || {},
    };
  } catch (err) {
    console.warn('[vitrine] getVitrineBlogIndex failed:', err.message);
    return { articles: [], categories: [], settings: {} };
  }
}

/**
 * Blog Ligobox — un article publié (corps, FAQ, points clés, connexes).
 * Renvoie null si non publié / inexistant.
 */
export async function getVitrineBlogArticle(slug) {
  try {
    const res = await fetch(
      cacheBust(`${API_URL}/api/vitrine/blog/${encodeURIComponent(slug)}`),
      { cache: CACHE_OPT },
    );
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (err) {
    console.warn('[vitrine] getVitrineBlogArticle failed:', err.message);
    return null;
  }
}

/**
 * Resout une URL de media (logo, favicon) en URL absolue.
 * Si l'URL commence par http(s), on la garde telle quelle.
 * Sinon on la prefixe avec API_URL.
 */
export function resolveMediaUrl(url) {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `${API_URL}${url}`;
  return `${API_URL}/${url}`;
}

/**
 * Reglages par defaut quand l'API n'est pas joignable.
 * Permet a la vitrine de s'afficher meme si le backend est down.
 */
function defaultSettings() {
  return {
    primary_color: '#e27228',
    secondary_color: '#db5400',
    logo_url: null,
    favicon_url: null,
    site_title: 'Ligobox',
    site_tagline: "L'ERP qui transforme votre PME du batiment",
    hero: {
      title: "L'ERP tout-en-un pour les pros du batiment",
      subtitle: "Devis, factures, projets, electricite, cuisine, dressing — gerez toute votre activite depuis une seule plateforme.",
      cta_text: 'Commencer gratuitement',
      image_url: null,
    },
    features: [
      { icon: 'Zap', title: 'Devis et factures en 2 minutes', description: 'Bibliotheque de templates, conversion devis → facture en un clic, signature electronique.' },
      { icon: 'Calculator', title: 'Conception electrique RGIE', description: 'Schemas unifilaires multi-tableaux, conformite RGIE, bibliotheques sectorielles.' },
      { icon: 'Box', title: 'Configurateur 3D Cuisine et Dressing', description: 'Concevez et chiffrez vos amenagements directement depuis l\'ERP.' },
      { icon: 'BarChart3', title: 'Tableaux de bord temps reel', description: 'Marges, tresorerie, rentabilite par chantier — tout en un coup d\'oeil.' },
      { icon: 'Building2', title: 'Multi-entreprise', description: 'Gerez plusieurs societes depuis un meme compte avec isolation totale des donnees.' },
      { icon: 'CreditCard', title: 'Paiements integres', description: 'Vos clients paient leurs factures par carte via Mollie ou Viva Wallet.' },
    ],
    packs_section: {
      title: 'Choisissez votre pack',
      subtitle: 'Des tarifs transparents, sans engagement. Changez de pack a tout moment.',
    },
    contact: {
      title: 'Une question ?',
      subtitle: 'Notre equipe vous repond sous 24h ouvrables.',
      email_destinataire: 'contact@ligobox.com',
    },
    seo: {
      meta_title: 'Ligobox — ERP pour les pros du batiment',
      meta_description: "L'ERP tout-en-un pour PME du batiment : devis, factures, electricite, cuisine, dressing, comptabilite.",
      og_image_url: null,
    },
    social_links: {
      facebook: '',
      linkedin: '',
      instagram: '',
    },
  };
}
